import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../helpers/axios";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import "./CreditAiStrip.css";

/**
 * AI Summary Strip for Credit Profile tab.
 * Shows traffic light signal, risk sentence, bureau vs bank EMI mismatch, expandable pros/cons.
 * Data comes from BSI results endpoint (pre-cached).
 */
export default function CreditAiStrip({ userId, appId }) {
  const decode = useCurrentUser();
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const role = decode?.role;
  const hasNoAccess = ["institute_admin", "institute_user", "channel_user", "collection"].includes(role);

  useEffect(() => {
    if (!userId || !appId || hasNoAccess) return;
    fetchAiSummary();
  }, [userId, appId]);

  const fetchAiSummary = async () => {
    setLoading(true);
    try {
      // Fetch both BSI and CIBIL data in parallel
      let bsiResult = null;
      let cibilData = null;

      const [bsiRes, cibilRes] = await Promise.allSettled([
        axiosInstance.get(`/bsi/results/${userId}/${appId}`),
        axiosInstance.get(`/cibil/report?userId=${userId}`),
      ]);

      if (bsiRes.status === "fulfilled") {
        bsiResult = bsiRes.value?.data || bsiRes.value?.data?.data || null;
      }
      if (cibilRes.status === "fulfilled") {
        const cibilSummary = cibilRes.value?.data?.data?.summary;
        if (cibilSummary && typeof cibilSummary === "object") {
          cibilData = cibilSummary;
        }
      }

      if (bsiResult || cibilData) {
        setData(buildMergedSummary(bsiResult, cibilData));
      } else {
        // Fallback: try Experian risk metrics
        try {
          const expRes = await axiosInstance.get(`/experian/risk/metrics?userId=${userId}`);
          const metrics = expRes?.data?.data;
          if (metrics) {
            setData(buildSummaryFromExperian(metrics));
          }
        } catch (err) { /* Experian not available either */ }
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const buildMergedSummary = (bsi, cibil) => {
    const foir = bsi?.adjustedFoir;
    const obligations = bsi?.hiddenObligations || [];
    const totalHidden = obligations.reduce((sum, o) => sum + (o.estimatedMonthlyAmount || 0), 0);

    // CIBIL metrics
    const cibilScore = cibil?.score || 0;
    const enquiries24m = cibil?.enquiries24Months || cibil?.totalEnquiries || 0;
    const enquiries30d = cibil?.enquiries30Days || 0;
    const scoringFactors = cibil?.scoringFactors || cibil?.scoring_factors || [];
    const bureauEmi = cibil?.obligations?.data?.meta?.eligibility?.obligationAtStart || cibil?.repaymentsPerMonth || foir?.bureauReportedEmis || 0;
    const activeAccounts = cibil?.activeNumberOfAccounts || 0;
    const totalAccounts = cibil?.totalNumberOfAccounts || 0;
    const closedAccounts = totalAccounts - activeAccounts;

    // Bank outflow from BSI (bureau EMI + hidden)
    const actualBankOutflow = Number(bureauEmi) + totalHidden;

    // Determine signal with approval probability
    let signal = "green";
    let signalIcon = "🟢";
    let approvalPct = 90;
    if (cibilScore > 0 && cibilScore < 600) {
      signal = "red"; signalIcon = "🔴"; approvalPct = 25;
    } else if (cibilScore < 700 || (foir?.adjustedFoir > 50) || totalHidden > 15000 || enquiries24m > 30) {
      signal = "orange"; signalIcon = "🟡"; approvalPct = 72;
    }
    // Adjust approval based on factors
    if (enquiries24m > 40) approvalPct = Math.min(approvalPct, 55);
    if (totalHidden > 20000) approvalPct -= 10;
    if (cibilScore > 750) approvalPct = Math.max(approvalPct, 85);

    const signalText = `${signal === "red" ? "High" : signal === "orange" ? "Medium" : "Low"} Risk — ${approvalPct}% Approval`;

    // Build risk sentence from CIBIL data
    const risks = [];
    if (enquiries24m > 10) risks.push(`${enquiries24m} enquiries in 24 months`);
    if (scoringFactors.length > 0) {
      const topFactor = typeof scoringFactors[0] === "string" ? scoringFactors[0] : scoringFactors[0]?.description || scoringFactors[0]?.factor || "";
      if (topFactor) risks.push(topFactor);
    }
    if (totalHidden > 0 && actualBankOutflow > Number(bureauEmi) * 1.5) {
      risks.push("Bureau EMI doesn't match bank outflow");
    } else if (totalHidden > 0) {
      risks.push(`Hidden EMIs ₹${totalHidden.toLocaleString("en-IN")}/mo`);
    }
    if (foir?.foirDelta > 5) risks.push(`FOIR gap +${Math.round(foir.foirDelta)}%`);
    const riskLine = risks.join(" • ") || "No significant risks detected";

    // Pros and cons
    const pros = [];
    const cons = [];
    if (cibilScore >= 700) pros.push(`CIBIL ${cibilScore} — above threshold`);
    if (cibil?.overdueAccount === 0 || cibil?.overdueCount === 0) pros.push("No overdue, write-offs, or settlements");
    if (cibil?.emiGraphCoordinates?.paid?.length > 0) pros.push("All EMIs on-time");
    if (closedAccounts > 0) pros.push(`${closedAccounts} accounts closed successfully`);
    if (obligations.length === 0) pros.push("No hidden obligations detected");

    if (enquiries24m > 20) cons.push(`${enquiries24m} enquiries in 24 months (credit hungry)`);
    if (enquiries30d > 5) cons.push(`${enquiries30d} enquiries in last 30 days`);
    if (totalHidden > 0) cons.push(`Bureau EMI ₹${Number(bureauEmi).toLocaleString("en-IN")} but bank shows ₹${actualBankOutflow.toLocaleString("en-IN")} outflow`);
    if (scoringFactors.length > 0) {
      scoringFactors.slice(0, 2).forEach(f => {
        const text = typeof f === "string" ? f : f?.description || f?.factor || "";
        if (text) cons.push(text);
      });
    }
    obligations.forEach(o => {
      cons.push(`${o.lenderIdentifier} — ₹${o.estimatedMonthlyAmount?.toLocaleString("en-IN")}/mo (${Math.round(o.detectionConfidence * 100)}% confidence)`);
    });

    return {
      signal, signalIcon, signalText,
      riskLine,
      bureauEmi: Number(bureauEmi),
      actualEmi: actualBankOutflow,
      hasMismatch: actualBankOutflow > Number(bureauEmi) * 1.2,
      mismatchLabel: "EMI: Bureau vs Bank",
      pros, cons,
      processedAt: bsi?.processedAt,
    };
  };

  const buildSummary = (bsi) => {
    const foir = bsi?.adjustedFoir;
    const obligations = bsi?.hiddenObligations || [];
    const totalHidden = obligations.reduce((sum, o) => sum + (o.estimatedMonthlyAmount || 0), 0);

    // Determine signal
    let signal = "green";
    let signalIcon = "🟢";
    let signalText = "Low Risk";
    if (foir?.adjustedFoir > 60) {
      signal = "red"; signalIcon = "🔴"; signalText = "High Risk";
    } else if (foir?.adjustedFoir > 45 || totalHidden > 10000) {
      signal = "orange"; signalIcon = "🟡"; signalText = "Medium Risk";
    }

    // Build risk sentence
    const risks = [];
    if (totalHidden > 0) risks.push(`Hidden EMIs ₹${totalHidden.toLocaleString("en-IN")}/mo`);
    if (foir?.foirDelta > 5) risks.push(`FOIR gap +${Math.round(foir.foirDelta)}%`);
    if (obligations.length > 2) risks.push(`${obligations.length} undisclosed lenders`);
    const riskLine = risks.join(" • ") || "No significant risks detected";

    // Pros and cons
    const pros = [];
    const cons = [];
    if (foir?.standardFoir < 50) pros.push("Standard FOIR within threshold");
    if (obligations.length === 0) pros.push("No hidden obligations detected");
    if (bsi?.processingStatus === "complete") pros.push("Full analysis completed");
    if (totalHidden > 0) cons.push(`Hidden obligations: ₹${totalHidden.toLocaleString("en-IN")}/mo`);
    if (foir?.foirDelta > 5) cons.push(`AI FOIR ${Math.round(foir.adjustedFoir)}% vs declared ${Math.round(foir.standardFoir)}%`);
    obligations.forEach(o => {
      cons.push(`${o.lenderIdentifier} — ₹${o.estimatedMonthlyAmount?.toLocaleString("en-IN")}/mo (${Math.round(o.detectionConfidence * 100)}% confidence)`);
    });

    return {
      signal, signalIcon, signalText,
      riskLine,
      bureauEmi: foir?.bureauReportedEmis || 0,
      actualEmi: (foir?.bureauReportedEmis || 0) + totalHidden,
      hasMismatch: totalHidden > 0,
      pros, cons,
      processedAt: bsi?.processedAt,
    };
  };

  const buildSummaryFromExperian = (metrics) => {
    let signal = "green";
    let signalIcon = "🟢";
    let signalText = "Low Risk";
    if (metrics.bureauScore > 0 && metrics.bureauScore < 600) {
      signal = "red"; signalIcon = "🔴"; signalText = "High Risk";
    } else if (metrics.bureauScore < 700 || metrics.maxDpd > 30 || metrics.totalEnquiries30Days > 5) {
      signal = "orange"; signalIcon = "🟡"; signalText = "Medium Risk";
    }

    const risks = [];
    if (metrics.bureauScore > 0 && metrics.bureauScore < 700) risks.push(`Experian Score: ${metrics.bureauScore}`);
    if (metrics.totalObligation > 0) risks.push(`Monthly Obligation: ₹${metrics.totalObligation.toLocaleString("en-IN")}`);
    if (metrics.totalEnquiries30Days > 3) risks.push(`${metrics.totalEnquiries30Days} enquiries in 30 days`);
    if (metrics.maxDpd > 0) risks.push(`Max DPD: ${metrics.maxDpd} days`);
    const riskLine = risks.join(" • ") || "No significant risks detected";

    const pros = [];
    const cons = [];
    if (metrics.bureauScore >= 700) pros.push(`Experian Score ${metrics.bureauScore} — above threshold`);
    if (metrics.overdueAccounts === 0) pros.push("No overdue accounts");
    if (!metrics.hasWriteOff) pros.push("No write-offs");
    if (!metrics.hasSettlement) pros.push("No settlements");
    if (metrics.bureauScore < 650) cons.push(`Low Experian Score: ${metrics.bureauScore}`);
    if (metrics.totalEnquiries30Days > 5) cons.push(`${metrics.totalEnquiries30Days} enquiries in 30 days (credit hungry)`);
    if (metrics.maxDpd > 30) cons.push(`Max DPD: ${metrics.maxDpd} days`);
    if (metrics.hasWriteOff) cons.push("Write-off detected");
    if (metrics.hasSettlement) cons.push("Settlement detected");
    if (metrics.overdueAccounts > 0) cons.push(`${metrics.overdueAccounts} overdue accounts`);

    return {
      signal, signalIcon, signalText,
      riskLine,
      bureauEmi: metrics.totalObligation || 0,
      actualEmi: metrics.totalObligation || 0,
      hasMismatch: false,
      pros, cons,
      processedAt: null,
    };
  };

  if (hasNoAccess || loading || !data) return null;

  const formatCurrency = (amount) => "₹" + Number(amount).toLocaleString("en-IN");

  return (
    <>
      <div className="credit-ai-strip">
        <span className="credit-ai-tag">AI</span>
        <div className="credit-ai-signal">
          <span className="credit-ai-signal-icon">{data.signalIcon}</span>
          <span className={`credit-ai-signal-text credit-ai-${data.signal}`}>{data.signalText}</span>
        </div>
        <div className="credit-ai-divider"></div>
        <div className="credit-ai-risk-line">{data.riskLine}</div>
        {data.hasMismatch && (
          <>
            <div className="credit-ai-divider"></div>
            <div className="credit-ai-mismatch">
              <div className="credit-ai-mismatch-label">{data.mismatchLabel || "EMI: Bureau vs Actual"}</div>
              <div className="credit-ai-mismatch-values">
                <span className="credit-ai-bureau">{formatCurrency(data.bureauEmi)}</span>
                {" → "}
                <span className="credit-ai-actual">{formatCurrency(data.actualEmi)}</span>
              </div>
            </div>
          </>
        )}
        <span className="credit-ai-expand" onClick={() => setExpanded(!expanded)}>
          {expanded ? "▾ Hide" : "▸ Details"}
        </span>
      </div>

      {expanded && (
        <div className="credit-ai-detail">
          <h4 className="credit-ai-detail-title">AI Credit Assessment (from BSI — 0 credits)</h4>
          <div className="credit-ai-pros-cons">
            <div>
              <ul>
                {data.pros.map((p, i) => <li key={i} className="credit-ai-pro">✓ {p}</li>)}
              </ul>
            </div>
            <div>
              <ul>
                {data.cons.map((c, i) => <li key={i} className="credit-ai-con">✗ {c}</li>)}
              </ul>
            </div>
          </div>
          <div className="credit-ai-cache-note">
            <span className="credit-ai-cache-dot"></span>
            From stored BSI analysis • 0 credits to view
          </div>
        </div>
      )}
    </>
  );
}
